import React, { useState } from 'react';
import { Input } from 'syngenta-digital-cropwise-react-ui-kit';

export interface CurrencyInputProps {
  value: number;
  onCommit: (value: number) => void;
  disabled?: boolean;
  dataTestId?: string;
  ariaLabel?: string;
  /** Defaults to `"$"`. Pass `undefined` for a non-currency decimal field (e.g. Planned Area). */
  prefix?: string;
  suffix?: React.ReactNode;
}

/**
 * A masked decimal input built on the modern
 * `syngenta-digital-cropwise-react-ui-kit` `Input` (no grow-ui) — the same
 * focus/raw-edit/blur-format behavior `FillInvoiceProductDrawer`'s cost
 * inputs already use: the raw in-progress string while focused (so the user
 * edits the real value, not a rounded display), and the value rounded to 2
 * decimals otherwise. Commits the parsed number to the caller on blur; the
 * caller owns any cross-field Formik writes (e.g. Total<->Per Ac sync),
 * since that math differs per field. Despite the name, also used for
 * non-currency decimal fields (Planned Area) via `prefix={undefined}` —
 * splitting out a separate component for that one field would just
 * duplicate this same masking logic.
 */
const CurrencyInput: React.FC<CurrencyInputProps> = ({
  value,
  onCommit,
  disabled = false,
  dataTestId,
  ariaLabel,
  prefix = '$',
  suffix
}) => {
  const [pendingEdit, setPendingEdit] = useState<string | undefined>(undefined);

  // FR-045: thousands separators for values >= 1,000 (toLocaleString is a
  // drop-in replacement for toFixed(2) below 1,000).
  const formattedValue = Number.isFinite(value)
    ? value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
    : '0.00';
  const displayValue = pendingEdit ?? formattedValue;

  return (
    <Input
      type="text"
      inputMode="decimal"
      prefix={prefix}
      suffix={suffix}
      disabled={disabled}
      data-testid={dataTestId}
      aria-label={ariaLabel}
      value={displayValue}
      onFocus={() =>
        setPendingEdit(Number.isFinite(value) ? value.toString() : '')
      }
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        const sanitized = e.target.value
          .replace(/[^0-9.]/g, '')
          .replace(/\.(?=.*\.)/g, '');
        setPendingEdit(sanitized);
      }}
      onBlur={() => {
        if (pendingEdit !== undefined) {
          onCommit(Number(pendingEdit) || 0);
        }
        setPendingEdit(undefined);
      }}
    />
  );
};

export default CurrencyInput;
